import { BRANDS, type Brand, type SnapshotImage } from "../b2c/data/brands";
import { theme } from "../brand";
import { BRIEFS } from "./briefs";
import { bestOn, contrast, isPale, mix } from "./color";

/**
 * A merchant, resolved for the video: live snapshot + creative brief +
 * colours derived from the logo. Everything a scene needs, nothing it has
 * to compute.
 */
export type Merchant = {
  slug: string;
  /** Display name ("SAINT LAZARE" → "Saint Lazare"). */
  name: string;
  domain: string;
  /**
   * The site to send people to, as printed on screen: the brief's `site`,
   * else the domain, else null when the domain is a back-office one
   * ("xyz.myshopify.com") nobody should type.
   */
  site: string | null;
  /** Max € per completed recommendation (live campaign estimate). */
  reward: number;
  logo: string;
  /** The logo file is an opaque square (its own tile): show it edge to edge. */
  logoOpaque: boolean;
  /** Photos in brief order, ≥ 1. `shots[0]` is the first frame. */
  shots: SnapshotImage[];
  tagline: { fr: string; en: string } | null;
  colors: {
    /** Behind the logo: its own tile colour, or white. */
    tile: string;
    /** The brand's colour: big surfaces (offer, end card). */
    brand: string;
    /** Text on `brand`. */
    onBrand: string;
    /** The brand's colour, readable as text on white. */
    onWhite: string;
    /** The brand's colour, readable on the dark photo backdrop. */
    onDark: string;
  };
};

const WHITE = theme.brand.colors.neutral.white;
const INK = theme.brand.colors.neutral.grey800;
const BLACK = "#000000";

const colorsFor = (b: Brand, override?: string): Merchant["colors"] => {
  const tile = b.palette?.tile ?? WHITE;
  const ink = b.palette?.ink ?? INK;
  const brand = override ?? (!isPale(tile) ? tile : ink);
  const onWhite =
    [brand, ink, mix(brand, BLACK, 0.45)].find(
      (c) => contrast(c, WHITE) >= 3,
    ) ?? INK;
  const onDark =
    [brand, mix(brand, WHITE, 0.45), mix(brand, WHITE, 0.7)].find(
      (c) => contrast(c, "#141414") >= 5,
    ) ?? WHITE;
  return { tile, brand, onBrand: bestOn(brand, [WHITE, INK]), onWhite, onDark };
};

const resolve = (b: Brand): Merchant => {
  const brief = BRIEFS[b.slug] ?? {};
  const gallery = b.heroes.length
    ? b.heroes
    : [{ src: b.hero!, width: 1200, height: 800 }];
  const picked = (brief.shots ?? [])
    .map((n) => gallery[n - 1])
    .filter((s): s is SnapshotImage => Boolean(s));
  return {
    slug: b.slug,
    name: b.label,
    domain: b.domain,
    site:
      brief.site ??
      (/myshopify\.com$/.test(b.domain)
        ? null
        : b.domain.replace(/^www\./, "")),
    reward: Math.round(b.reward),
    logo: b.logo!,
    logoOpaque: Boolean(b.palette?.tile),
    shots: picked.length ? picked : gallery,
    tagline: brief.tagline ?? null,
    colors: colorsFor(b, brief.color),
  };
};

/** Every live brand the video can be rendered for (logo, photo, reward). */
export const MERCHANTS: Merchant[] = BRANDS.map(resolve);

export const MERCHANT_SLUGS = MERCHANTS.map((m) => m.slug);

export const getMerchant = (slug: string): Merchant => {
  const m = MERCHANTS.find((x) => x.slug === slug);
  if (!m) {
    throw new Error(
      `Unknown merchant "${slug}". Live: ${MERCHANT_SLUGS.join(", ")} (npm run sync:brands to refresh).`,
    );
  }
  return m;
};

/** Photo `i`, wrapping around small galleries (1-photo brands reuse it). */
export const shot = (m: Merchant, i: number) => m.shots[i % m.shots.length];
